import { c as _c } from "react/compiler-runtime";
import { Draggable, type DroppableStateSnapshot } from "@hello-pangea/dnd";
import Edit from "@mui/icons-material/Edit";
import { Box, Chip, type ChipProps, IconButton, Skeleton, type SkeletonProps, useTheme } from "@mui/material";
import React from "react";
type SelectBarChipProps = ChipProps & {
  isCurrent: boolean;
  isEditable: boolean;
  isDragging?: boolean;
  onEditClick?: React.MouseEventHandler<HTMLButtonElement>;
  editLabel?: string;
};
export const SelectBarChip = React.forwardRef<HTMLDivElement, SelectBarChipProps>(function SelectBarChip(t0, ref) {
  const $ = _c(29);
  let editLabel;
  let isCurrent;
  let isDragging;
  let isEditable;
  let onEditClick;
  let restProps;
  let sx;
  if ($[0] !== t0) {
    ({
      isCurrent,
      isEditable,
      isDragging,
      onEditClick,
      editLabel,
      sx,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = editLabel;
    $[2] = isCurrent;
    $[3] = isDragging;
    $[4] = isEditable;
    $[5] = onEditClick;
    $[6] = restProps;
    $[7] = sx;
  } else {
    editLabel = $[1];
    isCurrent = $[2];
    isDragging = $[3];
    isEditable = $[4];
    onEditClick = $[5];
    restProps = $[6];
    sx = $[7];
  }
  const theme = useTheme();
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "&:hover": {
        ".EditIcon": {
          opacity: 1,
          pointerEvents: "all"
        }
      }
    };
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = `1px solid ${isCurrent ? "transparent" : theme.palette.divider}`;
  const t3 = isCurrent ? "primary.main" : "transparent";
  const t4 = isCurrent ? "primary.main" : "rgba(245,245,245,0.1)";
  let t5;
  if ($[9] !== t4) {
    t5 = {
      background: t4
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isDragging ? 4 : 0;
  const t7 = isDragging ? "blur(6px)" : "";
  let t8;
  if ($[11] !== sx || $[12] !== t2 || $[13] !== t3 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = {
      border: t2,
      background: t3,
      "&:hover": t5,
      cursor: "pointer !important",
      boxShadow: t6,
      backdropFilter: t7,
      ...sx
    };
    $[11] = sx;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== restProps || $[19] !== t8) {
    t9 = <Chip {...restProps} variant="filled" size="small" sx={t8} />;
    $[18] = restProps;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== editLabel || $[22] !== isEditable || $[23] !== onEditClick) {
    t10 = isEditable && onEditClick && <IconButton size="small" title={editLabel ?? "Edit"} className="EditIcon" sx={{
      transition: "opacity 0.3s",
      position: "absolute",
      top: "-10px",
      right: "-2px",
      opacity: 0,
      pointerEvents: "none",
      "&:hover": {
        "& svg": {
          opacity: 1
        }
      }
    }} onClick={onEditClick}><Edit sx={{
        transition: "opacity 0.3s",
        opacity: 0.3,
        fontSize: "1rem"
      }} /></IconButton>;
    $[21] = editLabel;
    $[22] = isEditable;
    $[23] = onEditClick;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== ref || $[26] !== t10 || $[27] !== t9) {
    t11 = <Box ref={ref} position="relative" sx={t1}>{t9}{t10}</Box>;
    $[25] = ref;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
});
type DraggableSelectBarChipProps = SelectBarChipProps & {
  id: string;
  index: number;
  droppableSnapshot: DroppableStateSnapshot;
};
export const DraggableSelectBarChip: React.FC<DraggableSelectBarChipProps> = t0 => {
  const $ = _c(15);
  let droppableSnapshot;
  let id;
  let index;
  let isEditable;
  let restProps;
  if ($[0] !== t0) {
    ({
      id,
      index,
      droppableSnapshot,
      isEditable,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = droppableSnapshot;
    $[2] = id;
    $[3] = index;
    $[4] = isEditable;
    $[5] = restProps;
  } else {
    droppableSnapshot = $[1];
    id = $[2];
    index = $[3];
    isEditable = $[4];
    restProps = $[5];
  }
  const t1 = !isEditable;
  let t2;
  if ($[6] !== droppableSnapshot.isDraggingOver || $[7] !== isEditable || $[8] !== restProps) {
    t2 = (provided, snapshot) => <SelectBarChip ref={provided.innerRef} isEditable={droppableSnapshot.isDraggingOver ? false : isEditable} isDragging={snapshot.isDragging} {...provided.draggableProps} {...provided.dragHandleProps} {...restProps} />;
    $[6] = droppableSnapshot.isDraggingOver;
    $[7] = isEditable;
    $[8] = restProps;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== id || $[11] !== index || $[12] !== t1 || $[13] !== t2) {
    t3 = <Draggable draggableId={id} index={index} isDragDisabled={t1}>{t2}</Draggable>;
    $[10] = id;
    $[11] = index;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export const SelectBarChipSkeleton: React.FC<SkeletonProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      borderRadius: 4,
      mt: "2px"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Skeleton width={64} height={24} variant="rectangular" sx={t0} {...props} />;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};